import { ChangeEvent, useCallback } from 'react';
import { useInstantSearch, useRefinementList } from 'react-instantsearch';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Sport } from '@sorare/core/src/__generated__/globalTypes';
import { Text16 } from '@sorare/core/src/atoms/typography';
import Switch from '@sorare/core/src/components/search/Switch';
import { useInSeasonLocalStorage } from '@sorare/core/src/hooks/search/useInSeasonLocalStorage';
import useCurrentSport from '@sorare/core/src/hooks/useCurrentSport';
import useEvents from '@sorare/core/src/lib/events/useEvents';
import {
  FILTERS,
  FilterWidget,
  TOGGLE_FILTERS,
} from '@sorare/core/src/lib/filters';
import { filters } from '@sorare/core/src/lib/glossary';
import {
  CURRENT_IN_SEASON_MLB,
  CURRENT_IN_SEASON_NBA,
} from '@sorare/core/src/lib/seasons';

const StyledText16 = styled(Text16)`
  display: inline-flex;
  align-items: center;
  gap: var(--unit);
`;

// This is a temporary widget refining on (hardcoded) `season:202{3,4}` until `in_season_eligible:true` is usable on US Sport cards. To be replaced by packages/football/src/components/searchCards/RefineInSeason/index.tsx when available.

const FILTER = TOGGLE_FILTERS.inSeasonFilter;

const Filter = () => {
  const { refine, items } = useRefinementList({
    attribute: FILTERS.season.attribute,
  });
  const track = useEvents();
  const { indexUiState } = useInstantSearch();
  const [, setInSeason] = useInSeasonLocalStorage();
  const sport = useCurrentSport();
  const CURRENT_IN_SEASON =
    sport === Sport.BASEBALL ? CURRENT_IN_SEASON_MLB : CURRENT_IN_SEASON_NBA;

  const handleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      track('Use Market Filter', {
        filterName: FILTER.attribute,
        filterValue: event.target.checked.toString(),
      });

      // clear all other season refinements
      items.forEach(refinement => {
        if (refinement.isRefined && refinement.value !== CURRENT_IN_SEASON) {
          refine(refinement.value);
        }
      });
      // and then refine on the current season
      refine(CURRENT_IN_SEASON);

      setInSeason(event.target.checked);
    },
    [track, items, refine, CURRENT_IN_SEASON, setInSeason]
  );

  // we rather consider the refinement state from the UI
  // so that we don't rely on the Algolia answer to come back to reflect the slider state
  const refinements =
    indexUiState.refinementList?.[FILTERS.season.attribute] || [];
  const optimisticRefined =
    refinements.length === 1 && refinements[0] === CURRENT_IN_SEASON;

  return (
    <Switch
      checked={optimisticRefined}
      onChange={handleChange}
      label={
        <StyledText16 bold>
          <svg
            width="26"
            height="16"
            viewBox="0 0 26 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <circle cx="21.7163" cy="4.57059" r="1.14286" fill="#4A484A" />
            <circle cx="24.0007" cy="1.14286" r="1.14286" fill="#4A484A" />
            <circle cx="19.4298" cy="8.00028" r="1.14286" fill="#4A484A" />
            <circle cx="17.1434" cy="11.428" r="1.14286" fill="#4A484A" />
            <circle cx="14.8589" cy="14.8577" r="1.14286" fill="#4A484A" />
            <circle cx="17.1429" cy="4.57059" r="1.14286" fill="#0169FD" />
            <circle cx="19.4293" cy="1.14286" r="1.14286" fill="#0169FD" />
            <circle cx="14.8584" cy="8.00028" r="1.14286" fill="#0169FD" />
            <circle cx="12.572" cy="11.428" r="1.14286" fill="#0169FD" />
            <circle cx="10.2875" cy="14.8577" r="1.14286" fill="#0169FD" />
            <circle cx="12.5714" cy="4.57059" r="1.14286" fill="#CE1614" />
            <circle cx="14.8578" cy="1.14286" r="1.14286" fill="#CE1614" />
            <circle cx="10.2869" cy="8.00028" r="1.14286" fill="#CE1614" />
            <circle cx="8.00046" cy="11.428" r="1.14286" fill="#CE1614" />
            <circle cx="5.71402" cy="14.8577" r="1.14286" fill="#CE1614" />
            <circle cx="8.00187" cy="4.57059" r="1.14286" fill="#FEDB5E" />
            <circle cx="10.2883" cy="1.14286" r="1.14286" fill="#FEDB5E" />
            <circle cx="5.71543" cy="8.00028" r="1.14286" fill="#FEDB5E" />
            <circle cx="3.431" cy="11.428" r="1.14286" fill="#FEDB5E" />
            <circle cx="1.14457" cy="14.8577" r="1.14286" fill="#FEDB5E" />
          </svg>
          {sport === Sport.BASEBALL ? (
            FILTER.label
          ) : (
            <FormattedMessage {...filters.latestSeasonLabel} />
          )}
        </StyledText16>
      }
    />
  );
};

const widget: FilterWidget = {
  key: FILTER.attribute,
  type: 'toggle',
  attribute: FILTER.attribute,
  Component: Filter,
};

export default widget;
